
import { Component, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { DataTableHeader } from 'vuetify';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import { DatabaseInterface, DatabaseRepositoryInterface, DatabaseUserInterface } from '@/services/Database/interfaces';
import DeleteDatabaseDialog from '@/components/dialogs/databases/DeleteDatabaseDialog.vue';
import ViewDatabaseDialog from '@/components/dialogs/databases/ViewDatabaseDialog.vue';
import DeleteDatabaseUserDialog from '@/components/dialogs/databases/DeleteDatabaseUserDialog.vue';
import ViewDatabaseUserDialog from '@/components/dialogs/databases/ViewDatabaseUserDialog.vue';
import WebsiteRepositoryInterface, { Website } from '@/services/Website/interfaces';
import SyncDatabaseSizeDialog from '@/components/dialogs/databases/SyncDatabaseSizeDialog.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  effectivePageSize,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    DeleteDatabaseDialog,
    ViewDatabaseDialog,
    ViewDatabaseUserDialog,
    DeleteDatabaseUserDialog,
    SyncDatabaseSizeDialog,
    PageHeader,
  },
  inject: [
    'databaseRepository',
    'serverRepository',
    'websiteRepository',
  ],
})
export default class AllDatabases extends Vue {
  public databaseRepository!: DatabaseRepositoryInterface;
  public serverRepository!: ServerRepositoryInterface;
  public websiteRepository!: WebsiteRepositoryInterface;

  public tableLoading: boolean = false;

  public LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  public options: ListPaginationOptions = defaultListOptions();
  public totalItems: number = 0;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public search: string = '';

  public servers: ServerInterface[] = [];
  public websites: Website[] = [];
  public databases: DatabaseInterface[] = [];
  public expanded: DatabaseUserInterface[] = [];
  public statuses: string[] = ['CREATING', 'CREATED', 'ERROR', 'ACTIVE', 'DISABLED'];

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Name', value: 'name' },
    { text: 'Website', value: 'websiteId' },
    { text: 'Server', value: 'server' },
    { text: 'Size (MB)', value: 'size' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions', sortable: false },
    { text: '', value: 'data-table-expand' },
  ];

  public expandedHeaders: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Username', value: 'username' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public deleteDatabaseDialog: string = '';
  public deleteDatabaseForDialog: DatabaseInterface | false = false;

  public viewDatabaseDialog: string = '';
  public viewDatabaseForDialog: number | false = false;

  public viewUserDialog: string = '';
  public viewUserForDialog: number | false = false;

  public deleteUserDialog: string = '';
  public deleteUserForDialog: DatabaseUserInterface | false = false;

  public syncSizeDialog: string = '';
  public databaseForSyncDialog: DatabaseInterface = {} as unknown as DatabaseInterface;

  public async created(): Promise<void> {
    try {
      this.applyRouteQuery();
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      this.options = parseListQuery(this.$route.query);
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public buildListQuery(): { [key: string]: string } {
    return buildListPaginationQuery(this.options);
  }

  public listQueryEquals(query: { [key: string]: string | (string | null)[] | null | undefined }): boolean {
    return listPaginationQueryEquals(query, this.options);
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = this.buildListQuery();
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'database_list', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async onSearch(): Promise<void> {
    this.options.page = 1;
    await this.syncRouteQuery();
    await this.initialize();
  }

  public async onPageChange(): Promise<void> {
    try {
      await this.syncRouteQuery();
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    try {
      await this.syncRouteQuery();
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'database_list') {
      return;
    }
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.applyRouteQuery();
    await this.initialize();
  }

  public async initialize(): Promise<void> {
    try {
      await this.getDatabases();
      await this.getServers();
      await this.getWebsites();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  public async getDatabases(): Promise<void> {
    this.tableLoading = true;
    this.databases = [];
    const search = this.getSearchString();
    [this.databases, this.totalItems] = await this.databaseRepository
        .getDatabases(this.options.page, search, undefined, effectivePageSize(this.options.itemsPerPage));
    this.tableLoading = false;
  }

  public async getServers(): Promise<void> {
    const ids = this.databases.map((database) => database.server);
    if (!ids.length) {
      return;
    }

    this.servers = await this.serverRepository.getServersByIds(ids);
  }

  public async getWebsites(): Promise<void> {
    const ids = this.databases.map((database) => database.websiteId);
    if (!ids.length) {
      return;
    }

    this.websites = await this.websiteRepository.getWebsitesByIds(ids);
  }

  public async viewUser(user: DatabaseUserInterface): Promise<void> {
    this.viewUserDialog = 'view-database-user-dialog';
    this.viewUserForDialog = user.id;
  }

  public async viewDatabase(database: DatabaseInterface): Promise<void> {
    this.viewDatabaseDialog = 'view-database-dialog';
    this.viewDatabaseForDialog = database.id;
  }

  public async editUser(user: DatabaseUserInterface): Promise<void> {
    await this.$router.push({ name: 'database_user_edit', params: { userId: String(user.id) } });
  }

  public deleteUser(user: DatabaseUserInterface): void {
    this.deleteUserDialog = 'delete-database-user-dialog';
    this.deleteUserForDialog = user;
  }

  public deleteDatabase(database: DatabaseInterface): void {
    this.deleteDatabaseDialog = 'delete-database-dialog';
    this.deleteDatabaseForDialog = database;
  }

  public getServerName(serverId: string): string {
    const server = this.servers.find((s) => s._id === serverId);
    if (!server) {
      return '';
    }

    return server.hostname;
  }

  public getWebsiteDomain(websiteId: number): string {
    const website = this.websites.find((w) => w.id === websiteId);
    if (!website) {
      return '';
    }

    return website.domain;
  }

  public async createDatabase(): Promise<void> {
    await this.$router.push({ name: 'database_create' });
  }

  public getSearchString(): string | undefined {
    if (this.search.trim() === '') {
      return undefined;
    }

    const searchServer = this.servers.find((server) => server.hostname === this.search.trim());
    if (searchServer) {
      return searchServer._id;
    }

    const searchWebsite = this.websites.find((w) => w.domain === this.search.trim());
    if (searchWebsite) {
      return searchWebsite.id.toString();
    }

    return this.search.trim();
  }

  public filterDatabases(database: DatabaseInterface): void {
    this.databases = this.databases.filter((db) => db.id !== database.id);
  }

  public filterUsers(user: DatabaseUserInterface): void {
    this.databases.forEach((database) => {
      database.users = database.users.filter((u) => u.id !== user.id);
    });
  }

  public showSyncSizeDialog(database: DatabaseInterface): void {
    this.databaseForSyncDialog = database;
    this.syncSizeDialog = 'sync-database-size-dialog';
  }

  public async syncSize(database: DatabaseInterface): Promise<void> {
    try {
      await this.databaseRepository.syncDatabaseSize(database.id);
      this.$infoDialog.show(`Synchronization of database ${database.name} started.`);
    } catch (err) {
      this.$errorDialog.show(`Failed to start size synchronization of database ${database.name}`);
    }
  }
}
